const internals = {};

exports.fromEvent = async function processImageUploadFromEvent(event, options = {}) {

    const { processImageFile } = internals;

    const {
        processingOptions = {},
        emergencyErrorCodes = [],
        onStart,
        onComplete
    } = options;

    const originalFile = event?.target?.files?.[0];

    if (!originalFile) {
        return {
            file: null,
            error: null,
            originalFile: null
        };
    }

    if (onStart) {
        onStart();
    }

    try {
        const file = await processImageFile(originalFile, processingOptions);

        return {
            file,
            error: null,
            originalFile
        };
    }
    catch (error) {
        const shouldThrow = emergencyErrorCodes.includes(error.code);

        if (shouldThrow) {
            throw error;
        }

        return {
            file: originalFile,
            error,
            originalFile
        };
    }
    finally {
        if (onComplete) {
            onComplete();
        }
    }
};

internals.processImageFile = async (file, options = {}) => {

    const {
        isHeicOrHeif,
        convertHeicFile,
        isAlreadyBrowserReadableError,
        compressImageFile
    } = internals;

    const {
        heicErrorMode = 'throw',
        compressionErrorMode = 'use-original',
        shouldCompress = () => true,
        compressionOptions = {
            maxSizeMB: 5,
            fileType: 'image/jpeg'
        }
    } = options;

    let nextFile = file;

    if (isHeicOrHeif(nextFile)) {
        try {
            nextFile = await convertHeicFile(nextFile);
        }
        catch (error) {
            const shouldIgnore = heicErrorMode === 'throw-unless-browser-readable' && isAlreadyBrowserReadableError(error);
            if (!shouldIgnore && heicErrorMode === 'throw-unless-browser-readable') {
                error.code = 'HEIC_CONVERSION_FAILED';
                throw error;
            }

            if (heicErrorMode === 'throw') {
                error.code = 'HEIC_CONVERSION_FAILED';
                throw error;
            }

            nextFile = file;
        }
    }

    if (shouldCompress(nextFile)) {
        const fileBeforeCompression = nextFile;

        try {
            nextFile = await compressImageFile(nextFile, compressionOptions);
        }
        catch (error) {
            if (compressionErrorMode === 'throw') {
                error.code = 'IMAGE_COMPRESSION_FAILED';
                throw error;
            }
            nextFile = fileBeforeCompression;
        }
    }

    return nextFile;
};

internals.makeJpegName = (fileName = 'image') => `${fileName.split('.')[0]}.jpeg`;
internals.isAlreadyBrowserReadableError = (error) => error.message?.toLowerCase().includes('already browser readable');
internals.isHeicOrHeif = (file) => {

    const extension = file?.name?.split('.').pop()?.toLowerCase();
    return ['heic', 'heif'].includes(extension);
};

internals.convertHeicFile = async (file) => {

    const { makeJpegName } = internals;
    const { heicTo } = await import(
        /* webpackChunkName: "image-heic" */
        'heic-to/csp'
    );

    const convertedBlob = await heicTo({
        blob: file,
        type: 'image/jpeg'
    });

    const normalizedBlob = Array.isArray(convertedBlob) ? convertedBlob[0] : convertedBlob;

    return new File([normalizedBlob], makeJpegName(file.name), {
        type: 'image/jpeg',
        lastModified: new Date()
    });
};

internals.compressImageFile = async (file, compressionOptions) => {

    const { makeJpegName } = internals;
    const { default: BrowserImageCompression } = await import(
        /* webpackChunkName: "image-compression" */
        'browser-image-compression'
    );

    const compressedFile = await BrowserImageCompression(file, compressionOptions);

    return new File([compressedFile], makeJpegName(file.name), {
        type: 'image/jpeg',
        lastModified: new Date()
    });
};
